// Utilities for rendering a lead's `customFields` blob on the leads list /
// lead detail surfaces.
//
// The raw `customFields` object that arrives from the API mixes three very
// different kinds of data together:
//   1. User-facing lead criteria the agent actually wants to see
//      (e.g. beneficiary, smoker, occupation, mortgage amount).
//   2. Internal / system plumbing that already has its own dedicated UI
//      (vendor info on the right-side panel, gclid + UTMs on lead detail via
//      the SourceAttribution component) or should never be shown at all
//      (apiKey, authToken, raw intake payloads, Mongo bookkeeping).
//   3. Empty placeholders ("null", "Not set", "", etc.).
//
// `formatCustomFields()` strips (2) and (3) and returns only (1), ready to
// render. Everything is data-driven via the sets/maps below so this stays
// easy to extend: to suppress a new internal field, add its key to
// INTERNAL_FIELDS; to give a field a nicer label, add it to FIELD_LABELS.

// ── Internal / system field keys to ALWAYS suppress ──────────────────────────
// These either have a dedicated UI surface elsewhere or are pure plumbing that
// should never reach the agent. Matching is case-insensitive (see norm()).
export const INTERNAL_FIELDS = new Set([
  // Vendor info — canonical home is the right-side status panel.
  'vendorId',
  'vendorName',
  'vendorTier',
  'costPerLead',
  // Click / ad attribution — shown on lead detail via SourceAttribution
  // (which reads lead.googleAds.* / lead.attribution.*, NOT this blob).
  'clickIds',
  'gclid',
  'gbraid',
  'wbraid',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  // Raw intake / serialization blobs — render as "[object Object]", never useful.
  'intakeMeta',
  'raw',
  'sourceMeta',
  'attribution',
  // Identifiers / secrets — internal only.
  'leadId',
  'externalId',
  'apiKey',
  'sid',
  'authToken',
  'source',
  // Mongo / ORM bookkeeping.
  '_id',
  '__v',
  'createdAt',
  'updatedAt',
]);

// ── Meta (Facebook) lead-gen internal IDs / metadata ─────────────────────────
// Meta leads arrive with a batch of platform plumbing keys (ledgen id, form id,
// page id, ad/adset/campaign ids, created time, …). These are internal Meta
// identifiers with no business meaning to an agent, so we suppress them just
// like the click/UTM attribution above. Unlike the click fields, Meta keys vary
// in casing and separator depending on the intake path, so they're matched by
// pattern rather than enumerated. Covers:
//   camelCase   metaLedgenId
//   snake_case  meta_ledgen_id
//   spaced      "meta ledgen id"
// while deliberately NOT matching user-answer keys like "meta_who_are_you_…".
const META_INTERNAL_RE =
  /^meta[_\s]?(ledgen|form|page|ad|adset|campaign|created|connection|ig_account|placement)[_\s]?(id|time|name)?$/i;

// ── Cost-style fields: keys where a 0 value means "empty / not set" ──────────
// For most fields a 0 is a legitimate value, but for money/cost fields a 0 is
// indistinguishable from "unset" and is just noise, so we hide it.
const COST_LIKE_FIELDS = new Set([
  'costPerLead',
  'cost',
  'saleAmount',
  'commission',
]);

// ── Human-friendly labels for known camelCase keys ───────────────────────────
// Anything not listed here falls back to humanize() (camelCase → "Title case").
export const FIELD_LABELS = {
  heightInches: 'Height (in)',
  weightLbs: 'Weight (lbs)',
  annualIncome: 'Annual income',
  preferredContactTime: 'Preferred contact time',
  mortgageAmount: 'Mortgage amount',
  ageRange: 'Age range',
  protectionGoal: 'Protection goal',
  productInterest: 'Product interest',
  beneficiary: 'Beneficiary',
  smoker: 'Smoker',
  occupation: 'Occupation',
};

// Case-insensitive key comparison so "VendorId" / "vendorid" are also caught.
function norm(key) {
  return String(key || '').trim().toLowerCase();
}

const INTERNAL_FIELDS_NORM = new Set([...INTERNAL_FIELDS].map(norm));

// True for any key that should be suppressed entirely: an explicit internal
// field (case-insensitive) OR a Meta lead-gen internal id/metadata key.
export function isInternalField(key) {
  const n = norm(key);
  if (INTERNAL_FIELDS_NORM.has(n)) return true;
  return META_INTERNAL_RE.test(n);
}

// Meta user-answer keys carry a "meta " / "meta_" / "metaXxx" prefix that's
// noise in the label. Strip it so "metaWhatIsYourAgeRange" reads as
// "What is your age range". Returns the key unchanged if it isn't Meta-prefixed.
function stripMetaPrefix(key) {
  return String(key || '').replace(/^meta[_\s]?/i, '');
}

// Meta answer values are often underscore-encoded multi-word strings
// ("yes,_but_not_enough"). Humanize ONLY those: a string that contains an
// underscore AND has no whitespace yet is treated as encoded and de-underscored.
// A normal sentence value ("I am not sure") is left untouched.
function humanizeValue(value) {
  if (typeof value !== 'string') return value;
  if (value.includes('_') && !/\s/.test(value)) {
    return value.replace(/_/g, ' ');
  }
  return value;
}

// Values that count as "empty" regardless of which field they belong to.
const EMPTY_STRINGS = new Set(['', 'null', 'undefined', 'not set', 'n/a']);

/**
 * True when a value should be treated as empty and hidden.
 * - null / undefined are always empty.
 * - Empty/placeholder strings ("", "null", "Not set", …) are always empty.
 * - Empty object {} and empty array [] are always empty.
 * - 0 is empty ONLY for cost-style fields (see COST_LIKE_FIELDS); elsewhere a
 *   0 may be a real answer so we keep it.
 */
export function isEmptyValue(value, key) {
  if (value === null || value === undefined) return true;

  if (typeof value === 'string') {
    return EMPTY_STRINGS.has(value.trim().toLowerCase());
  }

  if (typeof value === 'number') {
    if (Number.isNaN(value)) return true;
    if (value === 0 && COST_LIKE_FIELDS.has(key)) return true;
    return false;
  }

  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === 'object') return Object.keys(value).length === 0;

  return false;
}

// camelCase / snake_case key → "Sentence case" label.
function humanize(key) {
  const spaced = String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

// Render a single value to a display string. Objects/arrays would otherwise
// stringify to "[object Object]"; they're already filtered as internal/empty,
// but guard anyway so nothing slips through as garbage.
function displayValue(value) {
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

/**
 * Filter + format a lead's customFields for display.
 *
 * @param {object} customFields - raw key/value blob from the API.
 * @param {object} [opts]
 * @param {string} [opts.productInterest] - lead.productInterest; when set, a
 *        matching `productInterest` custom field is suppressed as redundant.
 * @returns {Array<{ key: string, label: string, value: string }>} only
 *          user-facing, non-empty fields, in their original key order.
 */
export function formatCustomFields(customFields, opts = {}) {
  if (!customFields || typeof customFields !== 'object') return [];

  const { productInterest } = opts;

  return Object.entries(customFields)
    .filter(([key, value]) => {
      if (isInternalField(key)) return false;
      if (isEmptyValue(value, key)) return false;
      // Drop a customFields productInterest that just mirrors the lead's own
      // productInterest column.
      if (
        norm(key) === 'productinterest' &&
        productInterest &&
        norm(value) === norm(productInterest)
      ) {
        return false;
      }
      return true;
    })
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key] || humanize(stripMetaPrefix(key)),
      value: displayValue(humanizeValue(value)),
    }));
}
